import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, ChevronRight, Copy, Loader2, RefreshCw, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { logsService, ApiLogEntry, getErrorMessage } from '@/services';

const PAGE_SIZE = 50;

/** Pretty JSON for objects, plain text otherwise. Nothing is cut or hidden: the log holds the complete values. */
function fmt(value: unknown): string {
  if (value === undefined || value === null || value === '') return '';
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

function isFailure(e: ApiLogEntry): boolean {
  return !!e.error || e.status === undefined || e.status >= 400;
}

export function Block({ title, value }: { title: string; value: unknown }) {
  const text = fmt(value);
  if (!text) return null;
  const copy = () => {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copiato'),
      () => toast.error('Copia non riuscita')
    );
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold uppercase text-dark-400">{title}</span>
        <button onClick={copy} className="text-dark-400 hover:text-dark-200" title="Copy">
          <Copy size={14} />
        </button>
      </div>
      <pre className="text-xs bg-dark-800 rounded p-3 overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-all">{text}</pre>
    </div>
  );
}

/** Method, URL and the four complete parts of one call (nothing hidden, nothing cut). */
export function ApiCallDetail({ entry }: { entry: ApiLogEntry }) {
  return (
    <div className="space-y-3">
      <div className="text-xs text-dark-300 break-all">
        <span className="font-semibold">{entry.method}</span> {entry.url}
        {entry.status !== undefined && <span className="ml-2 text-dark-400">→ {entry.status}</span>}
        {entry.durationMs !== undefined && <span className="ml-2 text-dark-500">{entry.durationMs} ms</span>}
      </div>
      <Block title="Request headers" value={entry.requestHeaders} />
      <Block title="Request body" value={entry.requestBody} />
      <Block title="Response headers" value={entry.responseHeaders} />
      <Block title="Response body" value={entry.responseBody} />
      <Block title="Error" value={entry.error} />
    </div>
  );
}

/** Loads one call by the id a panel log line carries (`context.apiLogId`) and shows it complete. */
export function ApiCallLoader({ id, admin = false }: { id: string; admin?: boolean }) {
  const [entry, setEntry] = useState<ApiLogEntry | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    logsService.getApiLog(id, admin).then(
      (e) => alive && setEntry(e),
      (err) => alive && setFailed(getErrorMessage(err))
    );
    return () => {
      alive = false;
    };
  }, [id, admin]);
  if (failed) return <div className="text-xs text-dark-400">Dettaglio non disponibile: {failed}</div>;
  if (!entry) return <div className="text-xs text-dark-400"><Loader2 size={14} className="animate-spin inline mr-1" />Carico richiesta e risposta...</div>;
  return <ApiCallDetail entry={entry} />;
}

/** The API call a panel log line refers to, if any. */
export function apiLogIdOf(context: string | null): string | null {
  if (!context) return null;
  try {
    const id = (JSON.parse(context) as { apiLogId?: unknown }).apiLogId;
    return typeof id === 'string' ? id : null;
  } catch {
    return null;
  }
}

export default function ApiLogList({ admin = false }: { admin?: boolean }) {
  const [entries, setEntries] = useState<ApiLogEntry[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [hidePolling, setHidePolling] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await logsService.listApiLogs(
        { search: debouncedSearch || undefined, errors_only: errorsOnly ? 1 : undefined, hide_polling: hidePolling ? 1 : undefined, page, limit: PAGE_SIZE },
        admin
      );
      setEntries(res.entries);
      setHasMore(res.hasMore);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, errorsOnly, hidePolling, page, admin]);

  useEffect(() => {
    load();
  }, [load]);

  const onSearch = (v: string) => {
    setSearch(v);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setDebouncedSearch(v);
      setPage(1);
    }, 300);
  };

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Canale, evento, chiamata, errore..."
            className="input pl-10 w-full"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-dark-300">
          <input type="checkbox" checked={errorsOnly} onChange={(e) => { setErrorsOnly(e.target.checked); setPage(1); }} />
          Solo errori
        </label>
        <label className="flex items-center gap-2 text-sm text-dark-300">
          <input type="checkbox" checked={hidePolling} onChange={(e) => { setHidePolling(e.target.checked); setPage(1); }} />
          Nascondi stats periodiche
        </label>
        <button onClick={load} disabled={isLoading} className="btn-secondary">
          {isLoading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
        </button>
      </div>

      <p className="text-xs text-dark-400">
        Ogni chiamata con richiesta e risposta complete, senza valori nascosti né troncati (token e password compresi). Conservazione: 7 giorni.
      </p>

      {entries.length === 0 && !isLoading ? (
        <div className="card text-center text-dark-400 py-8">Nessuna chiamata registrata</div>
      ) : (
        <div className="card p-0 divide-y divide-dark-700">
          {entries.map((e, i) => {
            const key = `${e.ts}-${i}`;
            const open = expanded.has(key);
            const bad = isFailure(e);
            const names = [e.context?.eventName, e.context?.channelName, e.context?.competitionName].filter(Boolean).join(' | ');
            return (
              <div key={key}>
                <button onClick={() => toggle(key)} className="w-full flex items-start gap-3 px-4 py-2 text-left hover:bg-dark-800">
                  {open ? <ChevronDown size={16} className="mt-1 shrink-0" /> : <ChevronRight size={16} className="mt-1 shrink-0" />}
                  <span className="text-xs text-dark-400 whitespace-nowrap mt-1">{dayjs(e.ts).format('DD/MM HH:mm:ss')}</span>
                  <span className={`text-xs px-2 py-0.5 rounded mt-0.5 ${bad ? 'bg-error-50 text-error-600' : 'bg-success-50 text-success-600'}`}>
                    {e.error ? 'ERR' : e.status ?? '—'}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="text-sm text-dark-100 break-all">{e.api}</span>
                    {names && <span className="block text-xs text-dark-400">{names}</span>}
                    {admin && <span className="block text-xs text-dark-500">utente {e.userId}</span>}
                    {e.error && <span className="block text-xs text-error-600 break-all">{e.error}</span>}
                  </span>
                  {e.durationMs !== undefined && <span className="text-xs text-dark-500 whitespace-nowrap mt-1">{e.durationMs} ms</span>}
                </button>
                {open && (
                  <div className="px-4 pb-4 space-y-3">
                    <ApiCallDetail entry={e} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-center items-center gap-3">
        <button className="btn-secondary btn-sm" disabled={page === 1 || isLoading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
          Precedente
        </button>
        <span className="text-sm text-dark-400">{page}</span>
        <button className="btn-secondary btn-sm" disabled={!hasMore || isLoading} onClick={() => setPage((p) => p + 1)}>
          Successiva
        </button>
      </div>
    </div>
  );
}
